import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Server, Key } from 'lucide-react';
import Footer from '../components/Footer';

const Security = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0a111e] flex flex-col">
      <div className="absolute inset-0 overflow-visible">
        <div className="absolute top-1/4 -left-1/4 w-[600px] h-[600px] bg-pink-500/20 rounded-full blur-[180px] animate-pulse" />
        <div className="absolute -bottom-1/4 -right-1/4 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-[180px] animate-pulse-slow" />
      </div>

      <button
        onClick={() => navigate('/')}
        className="fixed top-8 left-8 z-20 flex items-center gap-2 px-4 py-2 bg-[#0F1824] rounded-lg border border-white/[0.05] text-white hover:border-pink-500/50 transition-all duration-300 group"
      >
        <ArrowLeft className="h-5 w-5 text-pink-500 group-hover:transform group-hover:-translate-x-1 transition-transform" />
        <span>Back</span>
      </button>

      <main className="flex-1 relative z-10 py-16 px-4">
        <div className="max-w-4xl mx-auto mt-12">
          <div className="text-center mb-12">
            <Lock className="h-16 w-16 text-pink-500 mx-auto mb-6" />
            <h1 className="text-4xl font-bold text-white mb-4">Data Security</h1>
            <p className="text-xl text-gray-400">Enterprise-Grade Security for Your Peace of Mind</p>
          </div>

          <div className="bg-[#0F1824] rounded-2xl border border-white/[0.05] p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">Our Security Commitment</h2>
                <p className="text-gray-400">
                  At TiccAI, we understand that security is paramount when handling support tickets and accessing internal systems. We are developing a comprehensive security framework that adheres to industry best practices and standards. Our detailed security documentation is currently being finalized to provide complete transparency about our security measures and practices.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">Security Measures</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                  <div className="bg-white/[0.02] rounded-lg p-6 border border-white/[0.05]">
                    <div className="w-12 h-12 rounded-full bg-pink-500/10 flex items-center justify-center mb-4">
                      <Shield className="h-6 w-6 text-pink-500" />
                    </div>
                    <h3 className="text-white font-medium mb-2">Data Protection</h3>
                    <p className="text-gray-400 text-sm">
                      Enterprise-grade encryption for data at rest and in transit, with regular security audits and penetration testing.
                    </p>
                  </div>

                  <div className="bg-white/[0.02] rounded-lg p-6 border border-white/[0.05]">
                    <div className="w-12 h-12 rounded-full bg-pink-500/10 flex items-center justify-center mb-4">
                      <Server className="h-6 w-6 text-pink-500" />
                    </div>
                    <h3 className="text-white font-medium mb-2">Infrastructure Security</h3>
                    <p className="text-gray-400 text-sm">
                      Secure cloud infrastructure with multiple layers of security controls and continuous monitoring.
                    </p>
                  </div>

                  <div className="bg-white/[0.02] rounded-lg p-6 border border-white/[0.05]">
                    <div className="w-12 h-12 rounded-full bg-pink-500/10 flex items-center justify-center mb-4">
                      <Key className="h-6 w-6 text-pink-500" />
                    </div>
                    <h3 className="text-white font-medium mb-2">Access Control</h3>
                    <p className="text-gray-400 text-sm">
                      Strict access controls with multi-factor authentication and detailed audit logging.
                    </p>
                  </div>

                  <div className="bg-white/[0.02] rounded-lg p-6 border border-white/[0.05]">
                    <div className="w-12 h-12 rounded-full bg-pink-500/10 flex items-center justify-center mb-4">
                      <Lock className="h-6 w-6 text-pink-500" />
                    </div>
                    <h3 className="text-white font-medium mb-2">Compliance</h3>
                    <p className="text-gray-400 text-sm">
                      Commitment to maintaining compliance with relevant security standards and regulations.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">Current Security Practices</h2>
                <p className="text-gray-400">
                  While our comprehensive security documentation is being finalized, we maintain:
                </p>
                <ul className="mt-4 space-y-3">
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-pink-500/10 flex items-center justify-center shrink-0 mt-1">
                      <Lock className="h-3 w-3 text-pink-500" />
                    </div>
                    <span className="text-gray-400">End-to-end encryption for all data transmission</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-pink-500/10 flex items-center justify-center shrink-0 mt-1">
                      <Lock className="h-3 w-3 text-pink-500" />
                    </div>
                    <span className="text-gray-400">Regular security assessments and updates</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-pink-500/10 flex items-center justify-center shrink-0 mt-1">
                      <Lock className="h-3 w-3 text-pink-500" />
                    </div>
                    <span className="text-gray-400">Strict access controls and authentication measures</span>
                  </li>
                </ul>
              </section>

              <div className="mt-8 pt-8 border-t border-white/[0.05]">
                <p className="text-gray-400">
                  For any immediate security concerns or questions, please contact us at{' '}
                  <a href="mailto:business@ticcai.com" className="text-pink-500 hover:text-pink-400">
                    business@ticcai.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Security;